/**
 * WI Hero Engine — Responsive Height Modes, Ultra-Fast Transitions & Zero-Crash Architecture
 * Media-ratio rendering, reliable first-screen measurement, and Shopify editor-safe lifecycle handling.
 */
(() => {
  'use strict';

  if (window.__WIHeroEngineLoaded) return;
  window.__WIHeroEngineLoaded = true;

  const ROOT_SELECTOR = '.wi-hero[data-wi-hero-root]';
  const states = new WeakMap();
  const tabletMediaQuery = window.matchMedia('(max-width: 989px)');
  const phoneMediaQuery = window.matchMedia('(max-width: 749px)');
  const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const TOUCH_GESTURE = Object.freeze({
    axisLockDistance: 12,
    axisDominance: 1.2
  });

  const normalizeIndex = (index, length) => (Number(index) + length) % length;

  function getVideoSource(video) {
    if (!video) return '';
    const desktopSource = video.dataset.desktopSrc || '';
    const mobileSource = video.dataset.mobileSrc || '';
    const root = video.closest(ROOT_SELECTOR);
    const mobileMediaMax = Number(root?.dataset.mobileMediaMax) || 749;
    const useMobileMedia =
      mobileMediaMax >= 989 ? tabletMediaQuery.matches : phoneMediaQuery.matches;

    return useMobileMedia ? mobileSource || desktopSource : desktopSource || mobileSource;
  }

  function normalizeUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, document.baseURI).href;
    } catch {
      return url;
    }
  }

  const playPromises = new WeakMap();

  function safePlay(video) {
    if (!video || reduceMotionQuery.matches) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    try {
      const promise = video.play();
      if (promise !== undefined) {
        playPromises.set(video, promise);
        promise
          .then(() => {
            markVideoPlaying(video);
          })
          .catch((err) => {
            if (err.name === 'NotAllowedError') {
              // Browser autoplay policy held playback (e.g. Brave, Safari Low Power Mode)
              // The global one-time interaction listener will trigger on first user gesture.
            } else if (err.name !== 'AbortError') {
              console.warn('Hero video playback notice:', err.name);
            }
          })
          .finally(() => {
            playPromises.delete(video);
          });
      } else if (!video.paused) {
        markVideoPlaying(video);
      }
    } catch {}
  }

  function safePause(video) {
    if (!video) return;
    const pending = playPromises.get(video);
    if (pending) {
      pending
        .then(() => {
          try {
            video.pause();
          } catch {}
        })
        .catch(() => {});
    } else {
      try {
        video.pause();
      } catch {}
    }
  }

  function markVideoPlaying(video) {
    const slide = video?.closest('[data-wi-hero-slide]');
    if (!slide) return;
    slide.classList.add('is-video-playing');
  }

  function bindVideoEvents(video) {
    if (!video || video.dataset.eventsBound === 'true') return;
    video.dataset.eventsBound = 'true';

    video.addEventListener('playing', () => markVideoPlaying(video), { passive: true });
    video.addEventListener('timeupdate', () => {
      if (video.currentTime > 0) markVideoPlaying(video);
    }, { passive: true });
    video.addEventListener('ended', () => safePlay(video), { passive: true });

    if (!video.paused || video.currentTime > 0 || video.readyState >= 3) {
      markVideoPlaying(video);
    }
  }

  function prepareVideo(video, { play = false, preload = 'metadata' } = {}) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const source = getVideoSource(video);
    if (!source) return;

    bindVideoEvents(video);

    if (preload && video.preload !== preload) {
      video.preload = preload;
    }

    const currentSrc = normalizeUrl(video.currentSrc);
    const requestedSrc = normalizeUrl(source);

    // Guard against re-attaching .src if the browser is already processing child <source> tags
    const hasMatchingSourceTag = Array.from(video.querySelectorAll('source')).some(
      (s) => normalizeUrl(s.src) === requestedSrc
    );

    if (video.dataset.currentSrc !== source && currentSrc !== requestedSrc && !hasMatchingSourceTag) {
      video.dataset.currentSrc = source;
      video.src = source;
      try {
        video.load();
      } catch {}
    } else if (!video.dataset.currentSrc) {
      video.dataset.currentSrc = source;
    }

    if (play) {
      safePlay(video);
    }
  }

  function prepareSlide(slide, { play = false, preload = 'metadata' } = {}) {
    if (!slide) return;
    const video = slide.querySelector('[data-wi-hero-slide-video]');
    if (video) {
      prepareVideo(video, { play, preload });
    }
  }

  function getViewportHeight() {
    const visualHeight = window.visualViewport?.height;
    if (Number.isFinite(visualHeight) && visualHeight > 0) return visualHeight;
    return window.innerHeight || document.documentElement.clientHeight || 0;
  }

  function getDocumentTop(element) {
    if (!element) return 0;
    const rect = element.getBoundingClientRect();
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
    const top = rect.top + scrollY;
    return Number.isFinite(top) ? Math.max(0, top) : 0;
  }

  function getFixedHeaderBottom(root) {
    let bottom = 0;
    const candidates = document.querySelectorAll(
      'header, [data-header], .shopify-section-group-header-group'
    );

    candidates.forEach((element) => {
      if (!element || root?.contains(element)) return;
      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;

      const rect = element.getBoundingClientRect();
      if (rect.top <= 1 && rect.bottom > 0 && rect.bottom <= getViewportHeight()) {
        bottom = Math.max(bottom, rect.bottom);
      }
    });

    return bottom;
  }

  function updateHeroHeight(root, flowElement) {
    if (!root) return;

    if (root.dataset.heightMode !== 'first_screen') {
      root.style.removeProperty('--wi-hero-first-screen-height');
      return;
    }

    const viewportHeight = getViewportHeight();
    if (!Number.isFinite(viewportHeight) || viewportHeight <= 0) return;

    let topOffset = getDocumentTop(flowElement || root);

    // Fixed headers are not part of document flow, so account for them only when
    // the hero itself starts at the top of the document.
    if (topOffset <= 1) {
      topOffset = Math.max(topOffset, getFixedHeaderBottom(root));
    }

    const availableHeight = Math.max(1, Math.round(viewportHeight - topOffset));
    root.style.setProperty('--wi-hero-first-screen-height', `${availableHeight}px`);
  }

  function initHero(root) {
    if (!root || states.has(root)) return;

    const slider = root.querySelector('[data-wi-hero-slider]');
    if (!slider) return;

    const slides = Array.from(slider.querySelectorAll('[data-wi-hero-slide]'));
    if (!slides.length) return;

    const flowElement = root.closest('.shopify-section') || root;
    const dots = Array.from(root.querySelectorAll('[data-wi-hero-dot]'));
    const previousButton = root.querySelector('[data-wi-hero-prev]');
    const nextButton = root.querySelector('[data-wi-hero-next]');
    const statusRegion = root.querySelector('[data-wi-hero-status]');
    const autoplayToggle = root.querySelector('[data-wi-hero-autoplay-toggle]');
    const videoToggle = root.querySelector('[data-wi-hero-video-toggle]');

    const transitionDuration = Number(slider.dataset.transitionDuration) || 380;
    const isDesignMode = Boolean(window.Shopify?.designMode);
    const canAutoplaySlides = slider.dataset.autoplay === 'true' && !isDesignMode;
    const canAutoplayVideo = slider.dataset.videoAutoplay !== 'false';
    const interval = Math.max(Number(slider.dataset.interval) || 6000, 2500);

    const state = {
      currentIndex: Math.max(
        slides.findIndex((s) => s.classList.contains('is-active')),
        0
      ),
      timer: null,
      isHoverPaused: false,
      isFocusPaused: false,
      isPointerInteracting: false,
      isEditorPaused: false,
      isUserSlidePaused: false,
      isUserVideoPaused: !canAutoplayVideo,
      isAnimating: false,
      isInView: true,
      isDestroyed: false,
      transitionId: 0,
      intersectionObserver: null,
      resizeObserver: null,
      attributeObserver: null,
      layoutFrame: null,
      cleanup: [],
      animations: new Set()
    };

    const gesture = {
      pointerId: null,
      startX: 0,
      startY: 0,
      axis: null,
      direction: 0,
      hasMoved: false
    };

    const addListener = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options);
      state.cleanup.push(() => target.removeEventListener(type, handler, options));
    };

    const refreshHeroLayout = () => {
      if (state.isDestroyed) return;
      if (state.layoutFrame !== null) window.cancelAnimationFrame(state.layoutFrame);
      state.layoutFrame = window.requestAnimationFrame(() => {
        state.layoutFrame = null;
        if (!state.isDestroyed) updateHeroHeight(root, flowElement);
      });
    };

    const clearTimer = () => {
      if (state.timer !== null) {
        window.clearTimeout(state.timer);
        state.timer = null;
      }
    };

    // Only gates slideshow slide rotation timers — does NOT block active video playback
    const canAdvanceSlides = () =>
      !state.isDestroyed &&
      state.isInView &&
      !document.hidden &&
      !state.isHoverPaused &&
      !state.isFocusPaused &&
      !state.isPointerInteracting &&
      !state.isEditorPaused &&
      !state.isUserSlidePaused &&
      !reduceMotionQuery.matches;

    const activeSlide = () => slides[state.currentIndex];

    const pauseInactiveVideos = () => {
      slides.forEach((slide, idx) => {
        if (idx !== state.currentIndex) {
          safePause(slide.querySelector('[data-wi-hero-slide-video]'));
        }
      });
    };

    const updateSlideAccessibility = () => {
      const supportsInert = 'inert' in HTMLElement.prototype;
      slides.forEach((slide, idx) => {
        const isActive = idx === state.currentIndex;
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (supportsInert) {
          slide.inert = !isActive;
        }
      });
    };

    const updateDots = () => {
      dots.forEach((dot, idx) => {
        const isActive = idx === state.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    };

    const updateMotionControlsUI = () => {
      if (autoplayToggle) {
        autoplayToggle.setAttribute('aria-pressed', state.isUserSlidePaused ? 'true' : 'false');
      }
      if (videoToggle) {
        const currentHasVideo = activeSlide()?.dataset.hasVideo === 'true';
        videoToggle.hidden = !currentHasVideo;
        videoToggle.setAttribute('aria-pressed', state.isUserVideoPaused ? 'false' : 'true');
      }
    };

    const announceSlide = (idx = state.currentIndex) => {
      if (!statusRegion || isDesignMode) return;
      const slide = slides[idx];
      if (!slide) return;
      const label = slide.getAttribute('aria-label') || `Slide ${idx + 1} of ${slides.length}`;
      statusRegion.textContent = '';
      window.requestAnimationFrame(() => {
        if (!state.isDestroyed) statusRegion.textContent = label;
      });
    };

    const cancelTrackedAnimations = () => {
      state.animations.forEach((anim) => {
        try {
          anim.finish();
        } catch {}
      });
      state.animations.clear();
    };

    const trackAnimation = (anim) => {
      if (!anim) return null;
      state.animations.add(anim);
      anim.finished.catch(() => {}).finally(() => state.animations.delete(anim));
      return anim;
    };

    const clearMotionStyles = (slide) => {
      if (!slide) return;
      slide.classList.remove('is-motion-visible', 'is-motion-current', 'is-motion-target');
      slide.style.removeProperty('transform');
      slide.style.removeProperty('visibility');
      slide.style.removeProperty('z-index');
      slide.style.removeProperty('opacity');
    };

    const preloadUpcomingSlide = () => {
      if (slides.length < 2) return;
      const nextSlide = slides[normalizeIndex(state.currentIndex + 1, slides.length)];
      prepareSlide(nextSlide, { play: false, preload: 'auto' });
    };

    const scheduleNext = () => {
      clearTimer();
      if (!canAutoplaySlides || slides.length < 2 || !canAdvanceSlides()) return;
      state.timer = window.setTimeout(() => {
        goTo(state.currentIndex + 1, { direction: 1 });
      }, interval);
    };

    const resumeHero = () => {
      if (state.isDestroyed || !state.isInView || document.hidden) {
        clearTimer();
        return;
      }
      pauseInactiveVideos();
      if (!state.isUserVideoPaused) {
        prepareSlide(activeSlide(), { play: true, preload: 'auto' });
      }
      preloadUpcomingSlide();
      scheduleNext();
    };

    const finishTransition = (prevSlide, nextSlide, nextIdx, { announce = false } = {}) => {
      if (state.isDestroyed) return;

      prevSlide.classList.remove('is-active');
      nextSlide.classList.add('is-active');

      state.currentIndex = nextIdx;
      state.isAnimating = false;
      root.classList.remove('is-animating');

      clearMotionStyles(prevSlide);
      clearMotionStyles(nextSlide);
      cancelTrackedAnimations();

      updateSlideAccessibility();
      updateDots();
      updateMotionControlsUI();
      pauseInactiveVideos();

      preloadUpcomingSlide();
      scheduleNext();
      if (announce) announceSlide(nextIdx);
    };

    async function animateProgrammaticTransition(nextIdx, direction, options = {}) {
      if (state.isDestroyed) return;

      const prevSlide = slides[state.currentIndex];
      const nextSlide = slides[nextIdx];
      const transitionId = ++state.transitionId;

      state.isAnimating = true;
      clearTimer();
      cancelTrackedAnimations();
      root.classList.add('is-animating');

      if (!state.isUserVideoPaused) {
        prepareSlide(nextSlide, { play: true, preload: 'auto' });
      }

      nextSlide.classList.add('is-motion-visible', 'is-motion-target');
      prevSlide.classList.add('is-motion-current');

      nextSlide.style.visibility = 'visible';
      nextSlide.style.opacity = '1';
      nextSlide.style.zIndex = '3';
      prevSlide.style.zIndex = '2';

      const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';
      const duration = transitionDuration;

      const anims = [
        trackAnimation(
          prevSlide.animate(
            [
              { transform: 'translate3d(0, 0, 0)' },
              { transform: `translate3d(${-direction * 100}%, 0, 0)` }
            ],
            { duration, easing, fill: 'forwards' }
          )
        ),
        trackAnimation(
          nextSlide.animate(
            [
              { transform: `translate3d(${direction * 100}%, 0, 0)` },
              { transform: 'translate3d(0, 0, 0)' }
            ],
            { duration, easing, fill: 'forwards' }
          )
        )
      ];

      try {
        await Promise.all(anims.map((a) => a?.finished?.catch(() => {}) || Promise.resolve()));
      } finally {
        if (transitionId === state.transitionId && !state.isDestroyed) {
          finishTransition(prevSlide, nextSlide, nextIdx, { announce: options.announce });
        } else {
          state.isAnimating = false;
          root.classList.remove('is-animating');
        }
      }
    }

    function goTo(index, options = {}) {
      if (state.isDestroyed) return;

      const nextIdx = normalizeIndex(index, slides.length);
      if (nextIdx === state.currentIndex) {
        if (!state.isUserVideoPaused) {
          prepareSlide(activeSlide(), { play: true, preload: 'auto' });
        }
        resumeHero();
        return;
      }

      if (state.isAnimating) {
        cancelTrackedAnimations();
        slides.forEach(clearMotionStyles);
        state.isAnimating = false;
      }

      const direction = options.direction || (nextIdx > state.currentIndex ? 1 : -1);

      if (reduceMotionQuery.matches || typeof Element.prototype.animate !== 'function') {
        finishTransition(slides[state.currentIndex], slides[nextIdx], nextIdx, options);
        if (!state.isUserVideoPaused) {
          prepareSlide(slides[nextIdx], { play: true, preload: 'auto' });
        }
        return;
      }

      animateProgrammaticTransition(nextIdx, direction, options);
    }

    // Touch & Swipe mechanics
    if (slides.length > 1) {
      addListener(slider, 'pointerdown', (e) => {
        if (!e.isPrimary || e.pointerType === 'mouse') return;
        gesture.pointerId = e.pointerId;
        gesture.startX = e.clientX;
        gesture.startY = e.clientY;
        gesture.axis = null;
        gesture.hasMoved = false;
        state.isPointerInteracting = true;
        clearTimer();
      });

      addListener(slider, 'pointermove', (e) => {
        if (e.pointerId !== gesture.pointerId) return;
        const diffX = e.clientX - gesture.startX;
        const diffY = e.clientY - gesture.startY;

        if (!gesture.axis && Math.max(Math.abs(diffX), Math.abs(diffY)) >= TOUCH_GESTURE.axisLockDistance) {
          gesture.axis = Math.abs(diffX) > Math.abs(diffY) * TOUCH_GESTURE.axisDominance ? 'horizontal' : 'vertical';
          if (gesture.axis === 'horizontal') {
            try {
              slider.setPointerCapture(e.pointerId);
            } catch {}
          }
        }
        if (gesture.axis === 'horizontal') {
          gesture.direction = diffX < 0 ? 1 : -1;
          gesture.hasMoved = true;
        }
      }, { passive: true });

      const finishGesture = (e) => {
        if (e.pointerId !== gesture.pointerId) return;
        const diffX = e.clientX - gesture.startX;
        state.isPointerInteracting = false;
        gesture.pointerId = null;

        if (gesture.axis === 'horizontal' && Math.abs(diffX) > 28) {
          goTo(state.currentIndex + gesture.direction, { direction: gesture.direction, announce: true });
        } else {
          resumeHero();
        }
      };

      addListener(slider, 'pointerup', finishGesture);
      addListener(slider, 'pointercancel', finishGesture);

      addListener(slider, 'click', (e) => {
        if (gesture.hasMoved) {
          e.preventDefault();
          e.stopPropagation();
        }
        gesture.hasMoved = false;
      }, true);
    }

    // Keyboard navigation (WAI-ARIA Carousel Pattern)
    const dotList = root.querySelector('[data-wi-hero-dots]');
    if (dotList) {
      addListener(dotList, 'keydown', (e) => {
        const count = slides.length;
        let target = null;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          target = (state.currentIndex - 1 + count) % count;
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          target = (state.currentIndex + 1) % count;
        } else if (e.key === 'Home') {
          target = 0;
        } else if (e.key === 'End') {
          target = count - 1;
        }
        if (target !== null) {
          e.preventDefault();
          goTo(target, { announce: true });
          dots[target]?.focus();
        }
      });
    }

    dots.forEach((dot) => {
      addListener(dot, 'click', () => {
        const idx = Number(dot.dataset.slideIndex);
        if (!Number.isNaN(idx)) goTo(idx, { announce: true });
      });
    });

    if (previousButton) {
      addListener(previousButton, 'click', () => goTo(state.currentIndex - 1, { direction: -1, announce: true }));
    }
    if (nextButton) {
      addListener(nextButton, 'click', () => goTo(state.currentIndex + 1, { direction: 1, announce: true }));
    }

    if (autoplayToggle) {
      addListener(autoplayToggle, 'click', () => {
        state.isUserSlidePaused = !state.isUserSlidePaused;
        updateMotionControlsUI();
        if (state.isUserSlidePaused) clearTimer();
        else resumeHero();
      });
    }

    if (videoToggle) {
      addListener(videoToggle, 'click', () => {
        state.isUserVideoPaused = !state.isUserVideoPaused;
        updateMotionControlsUI();
        const activeVid = activeSlide()?.querySelector('[data-wi-hero-slide-video]');
        if (state.isUserVideoPaused) {
          safePause(activeVid);
        } else {
          safePlay(activeVid);
        }
      });
    }

    if (canAutoplaySlides && slides.length > 1) {
      addListener(root, 'focusin', () => { state.isFocusPaused = true; clearTimer(); });
      addListener(root, 'focusout', (e) => {
        if (e.relatedTarget && root.contains(e.relatedTarget)) return;
        state.isFocusPaused = false;
        scheduleNext();
      });
    }

    if (finePointerQuery.matches && slides.length > 1) {
      addListener(root, 'mouseenter', () => { state.isHoverPaused = true; clearTimer(); });
      addListener(root, 'mouseleave', () => { state.isHoverPaused = false; scheduleNext(); });
    }

    addListener(document, 'visibilitychange', () => {
      if (document.hidden) {
        clearTimer();
        pauseInactiveVideos();
        safePause(activeSlide()?.querySelector('[data-wi-hero-slide-video]'));
      } else {
        resumeHero();
      }
    });

    if ('IntersectionObserver' in window) {
      state.intersectionObserver = new IntersectionObserver((entries) => {
        state.isInView = Boolean(entries[0]?.isIntersecting);
        if (state.isInView) resumeHero();
        else pauseInactiveVideos();
      }, { rootMargin: '200px 0px', threshold: 0.01 });

      state.intersectionObserver.observe(root);
    }

    // Viewport resize tracking for all height modes. The scheduler is always active,
    // so switching First screen on/off repeatedly in the theme editor stays reliable.
    refreshHeroLayout();
    addListener(window, 'resize', refreshHeroLayout, { passive: true });
    addListener(window, 'orientationchange', refreshHeroLayout, { passive: true });
    if (window.visualViewport) {
      addListener(window.visualViewport, 'resize', refreshHeroLayout, { passive: true });
    }

    // Re-evaluate video sources across mobile, tablet, and desktop viewport changes
    const handleMediaBreakpointChange = () => {
      slides.forEach((slide) => {
        const video = slide.querySelector('[data-wi-hero-slide-video]');
        if (!video) return;
        const requested = getVideoSource(video);
        if (requested && video.dataset.currentSrc !== requested) {
          const wasPlaying = !video.paused;
          video.dataset.currentSrc = requested;
          video.src = requested;
          try {
            video.load();
          } catch {}
          if (wasPlaying || slide.classList.contains('is-active')) {
            safePlay(video);
          }
        }
      });
    };

    if ('addEventListener' in tabletMediaQuery) {
      tabletMediaQuery.addEventListener('change', handleMediaBreakpointChange);
      phoneMediaQuery.addEventListener('change', handleMediaBreakpointChange);
      state.cleanup.push(() => {
        tabletMediaQuery.removeEventListener('change', handleMediaBreakpointChange);
        phoneMediaQuery.removeEventListener('change', handleMediaBreakpointChange);
      });
    }

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(refreshHeroLayout);

      // Observe content that can change the hero's document position, but never
      // observe the hero's parent: doing so can create resize feedback loops.
      let sibling = flowElement.previousElementSibling;
      while (sibling) {
        state.resizeObserver.observe(sibling);
        sibling = sibling.previousElementSibling;
      }

      document
        .querySelectorAll('header, [data-header], .shopify-section-group-header-group')
        .forEach((element) => {
          if (element && !root.contains(element)) state.resizeObserver.observe(element);
        });
    }

    if ('MutationObserver' in window) {
      state.attributeObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.attributeName === 'data-height-mode')) {
          refreshHeroLayout();
        }
      });
      state.attributeObserver.observe(root, {
        attributes: true,
        attributeFilter: ['data-height-mode']
      });
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(refreshHeroLayout);
    }

    // Boot initial slide media
    if (!state.isUserVideoPaused) {
      prepareSlide(slides[state.currentIndex], { play: true, preload: 'auto' });
    }
    preloadUpcomingSlide();

    updateSlideAccessibility();
    updateDots();
    updateMotionControlsUI();

    state.goTo = goTo;
    state.refreshLayout = refreshHeroLayout;
    state.pauseAutoplay = () => {
      state.isEditorPaused = true;
      clearTimer();
    };
    state.resumeAutoplay = () => {
      state.isEditorPaused = false;
      resumeHero();
    };
    state.destroy = () => {
      state.isDestroyed = true;
      clearTimer();
      cancelTrackedAnimations();
      slides.forEach((s) => safePause(s.querySelector('[data-wi-hero-slide-video]')));
      state.intersectionObserver?.disconnect();
      state.resizeObserver?.disconnect();
      state.attributeObserver?.disconnect();
      if (state.layoutFrame !== null) window.cancelAnimationFrame(state.layoutFrame);
      state.cleanup.forEach((c) => c());
      states.delete(root);
    };

    states.set(root, state);
  }

  function initAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initHero(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initHero);
  }

  initAll();

  // One-time gesture listener to unlock autoplay on Brave & iOS Low Power Mode
  const unlockAutoplay = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const state = states.get(root);
      if (!state || state.isUserVideoPaused) return;
      const activeSlide = root.querySelector('.wi-hero__slide.is-active');
      const activeVid = activeSlide?.querySelector('[data-wi-hero-slide-video]');
      if (activeVid && activeVid.paused) {
        safePlay(activeVid);
      }
    });
  };
  window.addEventListener('pointerdown', unlockAutoplay, { once: true, passive: true });
  window.addEventListener('touchstart', unlockAutoplay, { once: true, passive: true });
  window.addEventListener('keydown', unlockAutoplay, { once: true, passive: true });

  // Theme Editor Lifecycle Integration
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    if (root) states.get(root)?.destroy?.();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    const state = states.get(root);
    if (state) {
      state.pauseAutoplay();
      state.refreshLayout?.();
      const activeSlide = root.querySelector('.wi-hero__slide.is-active');
      prepareSlide(activeSlide, { play: true, preload: 'auto' });
    }
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    states.get(root)?.resumeAutoplay?.();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const blockId = e.detail?.blockId;
    if (!blockId) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      const state = states.get(root);
      const slide = root.querySelector(`[data-block-id="${blockId}"]`);
      if (state && slide) {
        const idx = Number(slide.dataset.slideIndex);
        if (!Number.isNaN(idx)) {
          state.pauseAutoplay();
          state.goTo(idx);
          prepareSlide(slide, { play: true, preload: 'auto' });
        }
      }
    });
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.matches?.(ROOT_SELECTOR) ? e.target : e.target.querySelector?.(ROOT_SELECTOR);
    states.get(root)?.resumeAutoplay?.();
  });
})();